import { appUpdateModifySearchWidget } from "@actions/app.action";
import { availabilityInvalidSearchByQueryParams, availabilitySearchAllHome, availabilitySearchAllModify, availabilitySearchAll, availabilityRedirectToDohop, availabilitySearchByQueryParams } from "@actions/availability.action";
import { inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { DateRangePickerService } from "@components/controls/date-range-picker/date-range-picker.service";
import { BottomImageModalComponent } from "@components/modals/bottom-image-modal/bottom-image-modal.component";
import { StandardConfirmationMessageModalComponent } from "@components/modals/standard-confirmation-message-modal/standard-confirmation-message-modal.component";
import { TravelerPickerModalComponent } from "@components/modals/traveler-picker-modal/traveler-picker-modal.component";
import { COUNTRY_CODE_US, CultureKeyType, FALLBACK_CURRENCY, MAX_INFANT_AGE_YEARS } from "@constants/app.constants";
import { DATE_FORMAT_DEFAULT, DATE_FORMAT_YEAR_MONTH_DATE } from "@constants/date-format.constants";
import { DEFAULT_MAX_DATE, TripType } from "@constants/home.constants";
import { DEFAULT_SCHEDULE_LOADED_THRU_MESSAGE } from "@constants/search-widget.constants";
import { NullableString } from "@models/app.models";
import { AutoSearch } from "@models/auto-search.model";
import { PassengerTypeCode } from '@models/booking.models';
import { SearchWidgetCopy } from "@models/cms/search-widget.model";
import { FeatureFlagsEnum } from "@models/environment.type";
import { GrowthBookFeatureKeys } from "@models/growthbook.models";
import { RouteScheduleDate } from "@models/requests/route-schedule-request.model";
import { PassengerCriteria, PassengerTypesCount, SearchRequest, StationCriteria } from "@models/requests/search-request.model";
import { Station } from "@models/responses/station-response.model";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { DohopService } from "@services/do-hop/do-hop.service";
import { FeatureFlagService } from "@services/feature-flag/feature-flag.service";
import { GrowthBookService } from "@services/growthbook/growthbook.service";
import { ModalService } from "@services/modal/modal.service";
import { TolstoyService } from "@services/tolstoy/tolstoy.service";
import { UtilitiesService } from "@services/utilities/utilities.service";
import { WindowService } from "@services/window/window.service";
import { createDateFromDateLike, getCultureCodeParts } from "@utils/data-utils";
import { DateTime } from "luxon";
import { BehaviorSubject, combineLatest, filter, Observable, of, switchMap, take, takeUntil } from "rxjs";
import { StationPicker, DatesPicker } from "src/app/interfaces/search-widget-interface";

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  private dateRangePickerService = inject(DateRangePickerService);
  private onDestinationChangedSubmitTriggeredSubject = new BehaviorSubject<boolean>(false);

  onDestinationChangedSubmitTriggered$ = this.onDestinationChangedSubmitTriggeredSubject.asObservable();

  private readonly gbf = GrowthBookFeatureKeys;
  private readonly format = DATE_FORMAT_DEFAULT;

  private lastTripTypeSearched: TripType = TripType.roundTrip;
  tripTypeChangedFromPreviousSearch = false;

  isDateRangePickerEnabled = false;
  isTravelersStart = false;
  isPromoOnRoundTripEnabled = false;
  isPromoOnRoundTripVariant = '';
  isPromoFrontCampaignsEnabled = false;
  isPromoFrontCampaignsVariant = '';
  isSeatsBeforeBags = false;

  constructor(
    private modalService: ModalService,
    private store: Store,
    private featureFlagService: FeatureFlagService,
    private gbs: GrowthBookService,
    private doHopService: DohopService,
    private readonly tolstoyService: TolstoyService,
    private readonly utilitiesService: UtilitiesService
  ) {
    this.gbs.onChange$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.isDateRangePickerEnabled = this.gbs.growthBook.isOn(
          this.gbf.FEAT_US515_DATEPICKER_SEARCH_WIDGET_FRONTPAGE,
        ) &&
        this.gbs.growthBook.getFeatureValue(
          this.gbf.FEAT_US515_DATEPICKER_SEARCH_WIDGET_FRONTPAGE,
          '',
        ) === 'b';
      this.isTravelersStart = this.gbs.growthBook.isOn(this.gbf.FEAT_US458_TRAVELERS_FIRST_IN_FLOW) &&
        ['b', 'c'].includes(
          this.gbs.growthBook.getFeatureValue(
            this.gbf.FEAT_US458_TRAVELERS_FIRST_IN_FLOW,
            '',
          ),
        );
      this.isSeatsBeforeBags = this.gbs.isVariantOfExperimentEnabled('feat-002379-seatmap-PremiumUpgrade', 'b');
      this.isPromoOnRoundTripEnabled = this.gbs.growthBook.isOn(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP) &&
        ['b', 'c', 'd', 'e'].includes(
          this.gbs.growthBook.getFeatureValue(
            this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP,
            ''
          )
        );
      this.isPromoOnRoundTripVariant = this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US902_PROMO_CODE_ON_ROUND_TRIP, '');
      this.isPromoFrontCampaignsEnabled = this.gbs.growthBook.isOn(this.gbf.FEAT_US572_PROMOCODE_FRONT_CAMPAIGNS);
      this.isPromoFrontCampaignsVariant = this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US572_PROMOCODE_FRONT_CAMPAIGNS, '');
    });
  }

  private recalculateDobs(travelers: PassengerTypesCount): PassengerCriteria[] {
    const passengers: PassengerCriteria[] = [];
    const infantDobs: Date[] = [];

    const infantBirthYear = DateTime.utc().year - (MAX_INFANT_AGE_YEARS - 1);
    for (let i = 0; i < travelers.infantCount; i++) {
      infantDobs.push(DateTime.utc(infantBirthYear, 1, 1).toJSDate());
    }

    [
      { count: travelers.adultCount, type: PassengerTypeCode.ADT },
      { count: travelers.childCount, type: PassengerTypeCode.CHD },
      { count: travelers.infantInOwnSeatCount, type: PassengerTypeCode.INS },
      { count: travelers.youthCount, type: PassengerTypeCode.OTN }
    ].forEach(({ count, type }) => {
      if (count) {
        passengers.push({ type, count });
      }
    });

    if (infantDobs.length) {
      passengers.push({
        type: PassengerTypeCode.INF,
        count: infantDobs.length,
        dobs: infantDobs.map(dob => DateTime.fromJSDate(dob).toFormat(DATE_FORMAT_YEAR_MONTH_DATE)),
      });
    }

    return passengers;
  }

  private validate(travelers: PassengerTypesCount, cmsCopy: SearchWidgetCopy): boolean {

    const isAdtInfRatio =
      travelers.adultCount >=
      travelers.infantCount + travelers.infantInOwnSeatCount;
    if (!isAdtInfRatio) {
      this.modalService.show(BottomImageModalComponent, {
        initialState: {
          btnText: cmsCopy.okayCopy,
          text: 'We only allow one infant per adult.',
          img: cmsCopy.modalBottomImage.url,
          altText: cmsCopy.modalBottomImage.altText,
        },
      });
      return false;
    }

    const hasAdtWithChd = travelers.adultCount > 0 || travelers.childCount <= 0;
    if (!hasAdtWithChd) {
      this.modalService.show(BottomImageModalComponent, {
        initialState: {
          btnText: cmsCopy.okayCopy,
          text: cmsCopy.unaccompaniedMinorError,
          img: cmsCopy.modalBottomImage.url,
          altText: cmsCopy.modalBottomImage.altText,
        },
      });
      return false;
    }

    return true;
  }

  private dispatchHomeSearchAction(
    passengers: PassengerCriteria[],
    criteria: StationCriteria[],
    currencyCode: NullableString,
    previousCriteria: StationCriteria[] | undefined,
    activeStations: Station[] | null,
    infantDobs: Date[] | null,
    childDobs: Date[] | null,
    tripType: TripType,
    isFlightsPage: boolean,
    cmsCopy: SearchWidgetCopy,
    promoCode: string | null,
  ) {
    const actionPayload = {
      payload: {
        criteria,
        passengers: passengers,
        currencyCode: currencyCode ?? FALLBACK_CURRENCY,
        promotionCode: this.addPromoCodeForReturnTrip(
          promoCode,
          tripType,
        ),
        clearBooking: true,
        childDobs: childDobs,
        infantDobs: infantDobs,
      } as SearchRequest,
      tripType: tripType,
      isTravelersStart: this.isTravelersStart,
      isSeatsBeforeBags: this.isSeatsBeforeBags,
    };

    this.showOneWayFlightToUsaModalIfOneWayFlightToUsa$(
      criteria,
      previousCriteria,
      activeStations,
      cmsCopy,
      isFlightsPage,
    )
      .pipe(
        switchMap(() => this.dateRangePickerService.isSkipped$),
        take(1),
      )
      .subscribe((isSkipped) => {
        if (
          this.isDateRangePickerEnabled &&
          !isSkipped &&
          this.dateRangePickerService.firstDateFromRange$.getValue()
        ) {
          this.dispatchAvailabilitySearchByQueryParams(criteria, passengers, tripType);
          this.dateRangePickerService.onResetState({});
        }
        this.setIfTripTypeChanged(tripType);
        this.store.dispatch(availabilitySearchAllHome(actionPayload));
        this.utilitiesService.updateFlightSearchActionStarted();
      });
  }

  private showOneWayFlightToUsaModalIfOneWayFlightToUsa$(
    criteria: StationCriteria[],
    previousCriteria: StationCriteria[] | undefined,
    activeStations: Station[] | null,
    cmsCopy: SearchWidgetCopy,
    isFlightsPage: boolean,
  ): Observable<unknown> {
    const isSameCriteria =
      previousCriteria?.every((previousCriterion, criterionIndex) => {
        const currentCriterion = criteria[criterionIndex];
        return (
          currentCriterion?.origin === previousCriterion.origin &&
          currentCriterion?.destination === previousCriterion.destination
        );
      }) ?? false;

    // Don't re-show modal for the same criteria they just searched,
    // when not on home page.
    // I.e. when on home page, show modal even if search criteria is the
    // same as the previous search.
    if (isFlightsPage && isSameCriteria) {
      return of(true);
    }

    const isOneWayFlightToTheUsa =
      criteria.length === 1 &&
      criteria.some((criterion) => {
        const destinationStation = activeStations?.find(
          (station) => station.stationCode === criterion.destination,
        );
        return (
          destinationStation?.locationDetails.countryCode === COUNTRY_CODE_US
        );
      });

    if (!isOneWayFlightToTheUsa) {
      return of(true);
    }

    const usFlightModalRef = this.modalService.show(BottomImageModalComponent, {
      initialState: {
        btnText: cmsCopy.okayCopy,
        text: cmsCopy.oneWayFlightToUsaCopy,
        img: cmsCopy.modalBottomImage.url,
        altText: cmsCopy.modalBottomImage.altText,
      },
    });

    return usFlightModalRef?.onHidden?.pipe(take(1)) ?? of(true);
  }

  private dispatchAvailabilitySearchByQueryParams = (
    criteria: StationCriteria[],
    passengers: PassengerCriteria[],
    tripType: TripType,
  ) => {
    const actionPayload = {
      payload: {
        criteria,
        passengers,
        currencyCode: FALLBACK_CURRENCY,
        clearBooking: true,
      } as SearchRequest,
      isTravelersStart: this.isTravelersStart,
      isSeatsBeforeBags: this.isSeatsBeforeBags,
      tripType,
    };
    this.setIfTripTypeChanged(tripType);
    this.store.dispatch(availabilitySearchByQueryParams(actionPayload));
  };

  private handleFlightsPageSearch(
    criteria: StationCriteria[],
    currencyCode: NullableString,
    autoSearch: AutoSearch | undefined,
    isAnyPairDohop: boolean,
    isAnyRouteScheduleDateNorse: boolean,
    isModifyPath: boolean | undefined,
    travelers: PassengerTypesCount,
    cmsCopy: SearchWidgetCopy,
    searchRequest: SearchRequest | null,
    tripType: TripType,
    childDobs: Date[],
    infantDobs: Date[],
    isHomePage: boolean,
    isFlightsPage: boolean,
    activeStations: Station[] | null,
    promoCode: string | null
  ): void {
    const passengers = this.recalculateDobs(travelers);

    const isValid = this.validate(travelers, cmsCopy);
    if (!isValid) {
      this.store.dispatch(availabilityInvalidSearchByQueryParams());
      return;
    }

    const audultPaxCountInStore =
      searchRequest?.passengers?.find(
        (p) => p.type === PassengerTypeCode.ADT,
      )?.count ?? 0;
    const childPaxCountInStore =
      searchRequest?.passengers?.find(
        (p) => p.type === PassengerTypeCode.CHD,
      )?.count ?? 0;
    const infantPaxCountInStore =
      searchRequest?.passengers?.find(
        (p) => p.type === PassengerTypeCode.INF,
      )?.count ?? 0;

    const adultPaxCountCurrent =
      passengers?.find((p) => p.type === PassengerTypeCode.ADT)?.count ?? 0;
    const childPaxCountCurrent =
      passengers?.find((p) => p.type === PassengerTypeCode.CHD)?.count ?? 0;
    const infantPaxCountCurrent =
      passengers?.find((p) => p.type === PassengerTypeCode.INF)?.count ?? 0;

    const isPaxTypeCountMatching =
      audultPaxCountInStore === adultPaxCountCurrent &&
      childPaxCountInStore === childPaxCountCurrent &&
      infantPaxCountInStore === infantPaxCountCurrent;

    // TODO: Important - Cover these cases with specific tests so it will withstand with future code changes.
    // If in Home page then always clear the booking.
    // If in Flights page Modify flow and pax type count mismatch between store and local changes then clear the booking.
    const clearBooking =
      !isFlightsPage ||
      (isFlightsPage && !isPaxTypeCountMatching) ||
      autoSearch?.clearBooking;

    const actionPayload = {
      payload: {
        childDobs: childDobs,
        infantDobs: infantDobs,
        criteria: criteria.map((c, i) => ({
          ...c,
          productClass:
            this.featureFlagService.isFeatureActive(
              FeatureFlagsEnum.BUNDLE_RESTRICTION,
            ) && !clearBooking
              ? searchRequest?.criteria[i]?.productClass
              : undefined,
        })),
        passengers: passengers,
        currencyCode: currencyCode ?? FALLBACK_CURRENCY,
        promotionCode: this.addPromoCodeForReturnTrip(
          promoCode,
          tripType,
        ),
        clearBooking,
        isTravelersStart: this.isTravelersStart,
        isSeatsBeforeBags: this.isSeatsBeforeBags,
      } as SearchRequest,
      tripType: tripType,
    };
    this.setIfTripTypeChanged(tripType);
    if (
      !this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS) ||
      (this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS) &&
        !isAnyPairDohop &&
        isAnyRouteScheduleDateNorse)
    ) {
      if (isHomePage) {
        this.store.dispatch(availabilitySearchAllHome(actionPayload));
        this.utilitiesService.updateFlightSearchActionStarted();
      } else if (isFlightsPage) {
        this.store.dispatch(
          isModifyPath
          ? availabilitySearchAllModify(actionPayload)
          : availabilitySearchAll(actionPayload),
        );
        this.utilitiesService.updateFlightSearchActionStarted();
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      activeStations &&
        this.store.dispatch(
          availabilityRedirectToDohop({
            ...actionPayload,
            stations: activeStations,
          }),
        );
    }
  }

  private handleHomePageSearch(
    currencyCode: NullableString,
    culture: CultureKeyType,
    criteria: StationCriteria[],
    previousCriteria: StationCriteria[] | undefined,
    activeStations: Station[] | null,
    travelersCtrl: FormGroup,
    cmsCopy: SearchWidgetCopy,
    infantDobs: Date[] | null,
    childDobs: Date[] | null,
    lastSearchClicked: boolean,
    tripType: TripType,
    isFlightsPage: boolean,
    promoCode: string | null,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    lastSearchClicked
      ? this.dispatchHomeSearchAction(
          this.recalculateDobs(travelersCtrl?.value as PassengerTypesCount),
          criteria,
          currencyCode,
          previousCriteria,
          activeStations,
          infantDobs,
          childDobs,
          tripType,
          isFlightsPage,
          cmsCopy,
          promoCode,
        )
      : this.showTravelersPickerModal(
        culture,
        criteria,
        travelersCtrl,
        cmsCopy,
        infantDobs,
        childDobs,
        (passengers) => {
          this.setIfTripTypeChanged(tripType);
          this.dispatchHomeSearchAction(
            passengers,
            criteria,
            currencyCode,
            previousCriteria,
            activeStations,
            infantDobs,
            childDobs,
            tripType,
            isFlightsPage,
            cmsCopy,
            promoCode,
          );
          this.setDestinationChangedSubmitTriggered(true);
        },
        () => {
          this.setDestinationChangedSubmitTriggered(false);
        }
      );
  }

  private addPromoCodeForReturnTrip(
    promoCode: string | null,
    tripType: TripType,
  ): string {
    if (
      this.isPromoOnRoundTripEnabled &&
      !promoCode &&
      tripType === TripType.roundTrip
    ) {
      switch (this.isPromoOnRoundTripVariant) {
        case 'b':
          return 'RTD';
        case 'c':
          return 'RTD5';
        case 'd':
          return 'RTD10';
        case 'e':
          return 'RTD20';
        default:
          return ''
      }
    } else {
      return promoCode as string
    }
  }

  private getRouteMessage(
    c: StationCriteria,
    cmsCopy: SearchWidgetCopy,
    activeStations: Station[] | null,
    culture?: NullableString,
  ): NullableString {
    if (!cmsCopy) {
      return null;
    }

    const routeMessage = cmsCopy.routeMessages.find(
      (r) => r.origin === c.origin && r.destination === c.destination,
    );

    if (!routeMessage || (!routeMessage.startDate && !routeMessage.endDate)) {
      return null;
    }

    let message: NullableString = null;
    if (
      (!routeMessage.startDate ||
        new Date(routeMessage.startDate) <= new Date(c.beginDate)) &&
      routeMessage.endDate &&
      (routeMessage.startDate
        ? new Date(routeMessage.startDate) <= new Date(routeMessage.endDate)
        : true) &&
      new Date(routeMessage.endDate) < new Date(c.beginDate)
    ) {
      message = routeMessage.endMessage;
    } else if (
      (!routeMessage.endDate ||
        new Date(routeMessage.endDate) < new Date(c.beginDate) ||
        (routeMessage.startDate
          ? new Date(routeMessage.startDate) <= new Date(routeMessage.endDate)
          : true)) &&
      routeMessage.startDate &&
      new Date(routeMessage.startDate) > new Date(c.beginDate)
    ) {
      message = routeMessage.startMessage;
    } else {
      return null;
    }

    const routeOrgin =
      activeStations?.find(
        (station: Station) => station.stationCode == routeMessage.origin,
      ) ?? null;

    const routeDestination =
      activeStations?.find(
        (station: Station) => station.stationCode == routeMessage.destination,
      ) ?? null;

    return (
      message &&
      message
        .replace(
          '{origin}',
          `${routeOrgin?.fullName ?? ''} (${
            routeOrgin?.stationCode ?? routeMessage.origin
          })`,
        )
        .replace(
          '{destination}',
          `${routeDestination?.fullName ?? ''} (${
            routeDestination?.stationCode ?? routeMessage.destination
          })`,
        )
        .replace(
          '{startDate}',
          routeMessage.startDate
            ? DateTime.fromISO(routeMessage.startDate).toFormat(
                DATE_FORMAT_DEFAULT,
                {
                  locale: culture ?? undefined,
                },
              )
            : '',
        )
        .replace(
          '{endDate}',
          routeMessage.endDate
            ? DateTime.fromISO(routeMessage.endDate).toFormat(
                DATE_FORMAT_DEFAULT,
                {
                  locale: culture ?? undefined,
                },
              )
            : '',
        )
    );
  }

  private getRestrictionMessage(
    c: StationCriteria,
    countryCode: string | undefined,
    cmsCopy: SearchWidgetCopy,
  ): NullableString {
    if (!cmsCopy || !countryCode) {
      return null;
    }

    const countryRestriction = cmsCopy.countryStationsRestriction.find(
      (r) => r.countryCode === countryCode,
    );
    if (!countryRestriction) {
      return null;
    }

    const originRestriction = countryRestriction.restrictedStations.find(
      (s) => s.stationCode === c.origin,
    );
    const destinationRestriction = countryRestriction.restrictedStations.find(
      (s) => s.stationCode === c.destination,
    );
    let message: NullableString;
    if (!originRestriction && !destinationRestriction) {
      return null;
    } else {
      if (originRestriction?.restrictOrigin) {
        message = countryRestriction.message;
      }
      if (destinationRestriction?.restrictDestination) {
        message = countryRestriction.message;
      }
    }

    return message;
  }

  private showValidationModal(
    btnText: string,
    text: string,
    imgUrl: string,
    altText: string
  ) {
    return this.modalService.show(BottomImageModalComponent, {
      initialState: {
        btnText,
        text,
        img: imgUrl,
        altText,
      },
    });
  }

  private showConnectionsModal(
    currency: NullableString,
    culture: CultureKeyType,
    criteria: StationCriteria[],
    cmsCopy: SearchWidgetCopy,
    stationsWithConnections: Station[] | null,
    tripType: TripType,
    promotionCode: string,
    childDobs: Date[],
    infantDobs: Date[],
  ): void {
    const connectionsModalRef = this.modalService.show(
      StandardConfirmationMessageModalComponent,
      {
        class: 'modal-desktop-600',
        initialState: {
          primaryBtnText: cmsCopy.connectionsModalAccept,
          secondaryBtnText: cmsCopy.connectionsModalDecline,
          messageText: cmsCopy.connectionsModalText,
          titleText: cmsCopy.connectionsModalTitle,
        },
      },
    );

    connectionsModalRef.content?.response.pipe(take(1)).subscribe((value) => {
      if (
        value === 'primaryClick' &&
        stationsWithConnections &&
        currency
      ) {
        const request = {
          criteria,
          passengers: [],
          currencyCode: currency ?? FALLBACK_CURRENCY,
          promotionCode: promotionCode,
          clearBooking: true,
          childDobs: childDobs,
          infantDobs: infantDobs,
        } as SearchRequest;
        WindowService.open(
          this.doHopService.getDohopUrl(
            request,
            tripType,
            currency,
            getCultureCodeParts(culture).language,
            stationsWithConnections,
          ),
        );
      }
    });
  }

  private showTravelersPickerModal(
    culture: CultureKeyType,
    criteria: StationCriteria[],
    travelersCtrl: FormGroup,
    cmsCopy: SearchWidgetCopy,
    infantDobs: Date[] | null,
    childDobs: Date[] | null,
    onSuccess: (passengers: PassengerCriteria[]) => void,
    onFailureOrCancel: () => void,
  ): void {
    this.tolstoyService.hideTolstoyVideo();

    const travelerPickerModalRef = this.modalService.show(
      TravelerPickerModalComponent,
      {
        class: 'modal-desktop-600',
        initialState: {
          culture,
          cmsSearchWidgetCopy: cmsCopy,
          travelersFormGroup: travelersCtrl,
        },
      }
    );

    travelerPickerModalRef.onHidden?.pipe(take(1)).subscribe((reason: string) => {
      if (['backdrop-click', 'esc'].includes(reason)) {
        onFailureOrCancel();
        return;
      }
    });

    travelerPickerModalRef.content?.response?.pipe(take(1)).subscribe((response) => {
      if (!response) {
        onFailureOrCancel();
        return;
      }

      const travelers = travelersCtrl?.value as PassengerTypesCount;
      const passengers = this.recalculateDobs(travelers);

      if (!this.validate(travelers, cmsCopy)) {
        onFailureOrCancel();
        return;
      }

      const hasInfant = infantDobs && infantDobs?.length > 0;
      const hasChildUnder16 = childDobs?.some((dob) =>
        criteria[0]?.beginDate &&
        DateTime.fromFormat(criteria[0].beginDate, DATE_FORMAT_YEAR_MONTH_DATE)
          .diff(DateTime.fromJSDate(dob), 'years').years < 16
      );

      if ((hasInfant || hasChildUnder16) && !passengers.some(passenger => passenger.type === PassengerTypeCode.ADT)) {
        this.modalService.show(BottomImageModalComponent, {
          initialState: {
            btnText: cmsCopy.modify,
            text: cmsCopy.unaccompaniedMinorError,
            img: cmsCopy.modalBottomImage.url,
            altText: cmsCopy.modalBottomImage.altText,
          },
        });
        onFailureOrCancel();
        return;
      }

      onSuccess(passengers);
    });
  }

  setDestinationChangedSubmitTriggered(value: boolean): void {
    this.onDestinationChangedSubmitTriggeredSubject.next(value);
  }

  initSearchForm(
    stations$: Observable<Station[] | null> | Observable<Station[] | undefined>,
    stationsWithConnections$: Observable<Station[] | undefined>,
    culture$: Observable<CultureKeyType | undefined>,
    autoSearch$: Observable<AutoSearch>,
    searchInput$: Observable<SearchRequest | null>,
    tripType$: Observable<TripType>,
    lastSearchedCriteria$: Observable<StationCriteria[] | undefined>,
    selectedCurrencyCode$: Observable<string>,
    geolocatedCountryCode$: Observable<string | null>,
    destroy$: Observable<void>,
  ) {
    return combineLatest([stations$, stationsWithConnections$, culture$, autoSearch$, tripType$]).pipe(
      filter(([stations, stationsWithConnections, culture]) =>
        !!stations &&
        (this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS)
          ? !!stationsWithConnections
          : true) && !!culture,
      ),
      concatLatestFrom(() => [
        searchInput$,
        lastSearchedCriteria$,
        selectedCurrencyCode$,
        geolocatedCountryCode$,
      ]),
      takeUntil(destroy$),
    );
  }

  showPromoInput(
    cmsShowInput: boolean | null,
  ): boolean {
    if (this.isPromoFrontCampaignsEnabled && this.isPromoFrontCampaignsVariant) {
      return ['b', 'c'].includes(this.isPromoFrontCampaignsVariant);
    }
    return cmsShowInput ?? false;
  }

  isAutofillPromoCodeEnabled(cmsCopy: SearchWidgetCopy): boolean {
    return !!(cmsCopy.promoInputPromoCode
      && cmsCopy.promoInputPromoStartDate
      && cmsCopy.promoInputPromoEndDate
      && new Date(cmsCopy.promoInputPromoStartDate + 'Z') < new Date()
      && new Date(cmsCopy.promoInputPromoEndDate + 'Z') > new Date());
  }

  setPromoCode(
    promoCodeCntl: FormControl,
    cmsCopy: SearchWidgetCopy,
  ): void {
    const autofillPromo = this.isAutofillPromoCodeEnabled(cmsCopy);

    if (this.isPromoFrontCampaignsEnabled) {
      switch (this.isPromoFrontCampaignsVariant) {
        case 'a':
          if (autofillPromo) {
            promoCodeCntl.setValue(cmsCopy.promoInputPromoCode);
          }
          break;
        case 'c':
          if (autofillPromo && !promoCodeCntl?.value) {
            promoCodeCntl.setValue(cmsCopy.promoInputPromoCode);
          }
          break;
        default:
          break;
      }
    } else {
      if (cmsCopy.promoInputShow && autofillPromo && !promoCodeCntl?.value) {
        promoCodeCntl.setValue(cmsCopy.promoInputPromoCode);
      }
    }
  }

  onCancelModifySearch(): void {
    this.store.dispatch(
      appUpdateModifySearchWidget({
        showWidget: false,
        showLink: true,
      }),
    );
  }

  handleSearch(
    currencyCode: NullableString,
    culture: CultureKeyType,
    criteria: StationCriteria[],
    previousCriteria: StationCriteria[] | undefined,
    autoSearch: AutoSearch | undefined,
    isAnyPairDohop: boolean,
    isAnyRouteScheduleDateNorse: boolean,
    isModifyPath: boolean | undefined,
    activeStations: Station[] | null,
    travelersCtrl: FormGroup,
    cmsCopy: SearchWidgetCopy,
    infantDobs: Date[],
    childDobs: Date[],
    lastSearchClicked: boolean,
    promoCode: string | null,
    isFlightsPage: boolean,
    searchRequest: SearchRequest | null,
    tripType: TripType,
    isHomePage: boolean,
    stationsWithConnections: Station[] | null,
  ) {
    if (
      isAnyPairDohop &&
      this.gbs.growthBook.isOn(this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS) &&
      ['b', 'c'].includes(
        this.gbs.growthBook.getFeatureValue(
          this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS,
          '',
        ),
      )
    ) {
      this.showConnectionsModal(
        currencyCode,
        culture,
        criteria,
        cmsCopy,
        stationsWithConnections,
        tripType,
        promoCode as string,
        childDobs,
        infantDobs
      )
      return;
    }

    if (isHomePage) {
      this.handleHomePageSearch(
        currencyCode,
        culture,
        criteria,
        previousCriteria,
        activeStations,
        travelersCtrl,
        cmsCopy,
        infantDobs,
        childDobs,
        lastSearchClicked,
        tripType,
        isFlightsPage,
        promoCode,
      );
      return;
    }

    this.handleFlightsPageSearch(
      criteria,
      currencyCode,
      autoSearch,
      isAnyPairDohop,
      isAnyRouteScheduleDateNorse,
      isModifyPath,
      travelersCtrl.value as PassengerTypesCount,
      cmsCopy,
      searchRequest,
      tripType,
      childDobs,
      infantDobs,
      isHomePage,
      isFlightsPage,
      activeStations,
      promoCode,
    );
  }

  checkIfAnyPairDohop(criteria: StationCriteria[], activeStations: Station[] | null): boolean {
    return this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS) &&
      criteria.some((ond) => {
        const isMarketDohop = !!activeStations
          ?.find((s) => s.stationCode === ond.origin)
          ?.validMarkets?.find((m) => m.stationCode === ond.destination)
          ?.isDohop;

        return isMarketDohop;
      });
  }

  checkIfAnyRouteScheduleDateNorse(criteria: StationCriteria[], selectedRouteSchedule: Array<RouteScheduleDate[] | undefined>): boolean {
    return this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS) &&
      criteria.some((ond, i) => {
        const routeScheduleDepartDate = selectedRouteSchedule[i]?.find((s) => {
          const scheduleDayString = DateTime.fromJSDate(s.scheduleDay)
            .startOf('day')
            .toFormat(DATE_FORMAT_YEAR_MONTH_DATE);
          return scheduleDayString === ond.beginDate;
        });

        return !routeScheduleDepartDate?.isDohop;
      });
  }

  addReturnTripCriteria(criteria: StationCriteria[], selectedStations: StationPicker[], selectedDates: DatesPicker[]): void {
    criteria.push(
      ...selectedStations.map((s, i) => ({
        origin: s.destinationStationCode,
        destination: s.originStationCode,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        beginDate: DateTime.fromJSDate(
          createDateFromDateLike(selectedDates[i]?.returnDate, this.format) ?? new Date()
        ).toISODate()!,
      }))
    );
  }

  generateCriteria(selectedStations: StationPicker[], selectedDates: DatesPicker[]): StationCriteria[] {
    return selectedStations.map((s, i) => ({
      origin: s.originStationCode,
      destination: s.destinationStationCode,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      beginDate: DateTime.fromJSDate(
        createDateFromDateLike(selectedDates[i]?.departDate, this.format) ?? new Date()
      ).toISODate()!,
    }));
  }

  validateRouteDates(
    criteria: StationCriteria[],
    culture: CultureKeyType,
    countryCode: string | undefined,
    cmsCopy: SearchWidgetCopy,
    activeStations: Station[] | null,
  ): boolean {
    let routeDatesValid = true;

    criteria.forEach((c) => {
      let message = this.getRouteMessage(c, cmsCopy, activeStations, culture);
      if (!message) {
        message = this.getRestrictionMessage(c, countryCode, cmsCopy);
      }

      if (message) {
        routeDatesValid = false;
        this.setDestinationChangedSubmitTriggered(false);
        this.modalService.show(BottomImageModalComponent, {
          initialState: {
            btnText: cmsCopy.modify,
            text: message,
            img: cmsCopy.modalBottomImage.url,
            altText: cmsCopy.modalBottomImage.altText,
          },
        });
      }
    });

    return routeDatesValid;
  }

  validateFormAndShowErrors(
    cmsCopy: SearchWidgetCopy,
    searchWidgetForm: FormGroup,
    travelersCtrl: FormGroup,
    stationsCtrl: FormArray,
    datesCtrl: FormArray,
    autoSearch?: { search: boolean },
  ): boolean {
    if (!searchWidgetForm.valid) {
      if (travelersCtrl.invalid) {
        this.setDestinationChangedSubmitTriggered(false);
        this.showValidationModal(
          cmsCopy.select,
          cmsCopy.paxValidationError,
          cmsCopy.modalBottomImage.url,
          cmsCopy.modalBottomImage.altText
        );
        return false;
      }

      if (stationsCtrl.invalid) {
        this.setDestinationChangedSubmitTriggered(false);
        const modalRef = this.showValidationModal(
          cmsCopy.okayCopy,
          cmsCopy.invalidStations,
          cmsCopy.modalBottomImage.url,
          cmsCopy.modalBottomImage.altText
        );

        if (autoSearch?.search) {
          modalRef.onHide?.pipe(take(1)).subscribe(() => {
            this.store.dispatch(availabilityInvalidSearchByQueryParams());
          });
        }
        return false;
      }

      if (datesCtrl.invalid) {
        this.setDestinationChangedSubmitTriggered(false);
        this.showValidationModal(
          cmsCopy.return,
          cmsCopy.invalidDates,
          cmsCopy.modalBottomImage.url,
          cmsCopy.modalBottomImage.altText
        );
        return false;
      }
      return false;
    }
    return true;
  }

  updateDatesFromPreviousCriteria(
    previousCriteria: Array<{ beginDate: string }> | undefined,
    datesCtrl: FormArray,
  ): void {
    const _dates: DatesPicker = {
      departDate: new Date(),
      returnDate: new Date(),
    };

    previousCriteria?.forEach((c, i) => {
      if (i === 0) {
        _dates.departDate = new Date(c.beginDate);
      } else if (i === 1) {
        _dates.returnDate = new Date(c.beginDate);
      }
    });

    datesCtrl.controls.forEach((dateControl) => {
      dateControl.setValue(_dates);
    });
  }

  isDateBeyondSchedule(selectedDates: DatesPicker[], scheduleLoadedThru: string,): boolean {
    const maxSearchDate = scheduleLoadedThru
      ? DateTime.fromFormat(scheduleLoadedThru, DATE_FORMAT_YEAR_MONTH_DATE)
          .plus({ days: 1 })
          .toJSDate()
      : DEFAULT_MAX_DATE;

    return selectedDates.some((d) => d.departDate >= maxSearchDate);
  }

  showScheduleErrorModal(
    cmsCopy: SearchWidgetCopy,
    scheduleLoadedThru: string,
  ): void {
    this.setDestinationChangedSubmitTriggered(false);
    this.modalService.show(BottomImageModalComponent, {
      initialState: {
        btnText: cmsCopy.modify,
        text: (cmsCopy.scheduleLoadedThruMessage ?? DEFAULT_SCHEDULE_LOADED_THRU_MESSAGE)
          .replace(
            '{scheduleLoadedThruDate}',
            DateTime.fromFormat(
              scheduleLoadedThru ?? DEFAULT_MAX_DATE.toDateString(),
              DATE_FORMAT_YEAR_MONTH_DATE,
            ).toFormat(DATE_FORMAT_DEFAULT),
          ),
        img: cmsCopy.modalBottomImage.url,
        altText: cmsCopy.modalBottomImage.altText,
      },
    });
  }

  handleSkipReturn() {
    this.setIfTripTypeChanged(TripType.oneWay);
  }

  private setIfTripTypeChanged(tripType: TripType) {
    if (this.lastTripTypeSearched !== tripType) {
      this.lastTripTypeSearched = tripType;
      this.tripTypeChangedFromPreviousSearch = true;
    }
  }
}
